<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';

import { AuthState } from '@clovyr/pollen/pollen';

import { useEventBus } from '@/stores/event_bus';
import { usePollenStore } from '@/stores/pollen_store';

import SecretKeyWarning from '../../components/elements/SecretKeyWarning.vue';
import { useRecoveryKit } from '../../composables/useRecoveryKit';

const { authState, isUserAuthenticated } = storeToRefs(usePollenStore());

export type RecoveryKitDownloadProps = {
  isPubFlow?: boolean;
};
defineProps<RecoveryKitDownloadProps>();

const { downloadRecoveryKit, downloading, error } = useRecoveryKit();

const router = useRouter();

const { eventBus } = useEventBus();

const routeToPublisherPortal = () => {
  eventBus.off('modal:login:close');
  if (isUserAuthenticated.value && authState.value === AuthState.LoggedIn) {
    void router.push({
      path: '/publishers/new',
    });
  }
};
</script>

<template>
  <div class="recovery-view">
    <div class="title">Clovyr Account Created.</div>
    <div class="title">Save Your Recovery Kit.</div>
    <div class="headline">
      Your account has been created successfully! Now download your Recovery Kit, it contains your
      Secret Key for unlocking your encrypted app library on a new device or if your usual sign-in
      method cannot unlock it. Store the file somewhere safe.
    </div>

    <SecretKeyWarning />

    <Button
      :label="downloading ? 'Preparing PDF…' : 'Download PDF'"
      :disabled="downloading"
      type="button"
      class="pdf-button"
      :reversed="true"
      @click="downloadRecoveryKit"
    />
    <p v-if="error" role="alert">{{ error }}</p>
    <Button
      v-if="isPubFlow"
      label="close and go to publisher portal"
      type="submit"
      translucent
      class="pub-button"
      @click="routeToPublisherPortal"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';

.recovery-view {
  max-width: 30rem;

  .title {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: -0.03em;
  }
  .headline {
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0em;
    text-align: left;
    color: colors.color(grey, secondary);
    margin: 20px 0 20px 0;
  }
  .pdf-button {
    width: 100%;
    font-weight: 500;
  }
  .pub-button {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  > .button {
    margin-top: 1rem;
  }
}
</style>
